import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import { Config } from '../types';
import createMyTheme from '../themes/theme';
import { Theme } from '@mui/material/styles';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useUser } from './UserContext';
import { useSnackbar } from 'notistack';
import { enqueueUniqueSnackbar } from '../utilities/snackbarUtils';
import ExpandableErrorSnackbar from '../components/common/ExpandableErrorSnackbar';

// Create a type that combines Config with the update function
type ConfigContextType = Config & {
  updateConfig: (updates: Partial<Config>) => void;
};

const ConfigContext = createContext<ConfigContextType | null>(null);

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return context;
};

/** Returns config when inside ConfigProvider, null otherwise. Use for optional branding (e.g. mobile block screen). */
export const useOptionalConfig = (): ConfigContextType | null => {
  return useContext(ConfigContext);
};

interface ConfigProviderProps {
  children: ReactNode;
  configUrlParam?: string;
}

// Keys for session storage
const ADMIN_SETTINGS_KEY = 'culturemap-admin-settings';

export const ConfigProvider: React.FC<ConfigProviderProps> = ({
  children,
  configUrlParam = "config",
}) => {
  const [config, setConfig] = useState<Config | null>(null);
  const [theme, setTheme] = useState<Theme | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { profile, isInitialized } = useUser();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Helper function to show expandable error snackbars
  const showConfigError = (simplifiedMessage: string, fullErrorDetails: string) => {
    const snackbarKey = enqueueSnackbar('', {
      persist: false,
      autoHideDuration: 10000,
      content: (key) => (
        <ExpandableErrorSnackbar 
          id={key}
          message={simplifiedMessage}
          fullErrorDetails={fullErrorDetails}
          dismissSnackbar={() => closeSnackbar(key)}
        />
      ),
    });
    return snackbarKey;
  };

  const validateConfig = (config: any): config is Config => {
    if (!config || typeof config !== 'object') {
      throw new Error('Invalid config format: Not an object');
    }
    
    // Basic validation for essential properties
    if (!config.appTitle) {
      throw new Error('Invalid config format: Missing appTitle');
    }
    
    // Validate map layers if they exist
    if (config.mapLayers && Array.isArray(config.mapLayers)) {
      for (let i = 0; i < config.mapLayers.length; i++) {
        const layer = config.mapLayers[i];
        
        // Validate layer ID
        if (!layer.id) {
          throw new Error(`Invalid map layer at index ${i}: Missing id`);
        }
        
        // Validate layer style expressions
        if (layer.style) {
          // Check for zoom-dependent interpolate expressions
          Object.entries(layer.style).forEach(([key, value]) => {
            if (Array.isArray(value) && value[0] === 'interpolate' && Array.isArray(value[2]) && value[2][0] === 'zoom') {
              // For interpolate expressions, check that zoom levels are in ascending order
              const zoomLevels = [];
              for (let j = 3; j < value.length; j += 2) {
                zoomLevels.push(value[j]);
              }
              
              // Check if zoom levels are in ascending order
              for (let j = 1; j < zoomLevels.length; j++) {
                if (zoomLevels[j] <= zoomLevels[j-1]) {
                  throw new Error(
                    `Invalid config in layer "${layer.id}", style property "${key}": ` +
                    `Zoom levels in interpolate expression must be in ascending order. ` +
                    `Found ${zoomLevels[j-1]} followed by ${zoomLevels[j]}.`
                  );
                }
              }
            }
          });
        }
      }
    }
    
    return true;
  };

  // Load admin settings from session storage
  const loadAdminSettings = () => {
    try {
      const storedSettings = sessionStorage.getItem(ADMIN_SETTINGS_KEY);
      if (storedSettings) {
        return JSON.parse(storedSettings);
      }
    } catch (error) {
      console.warn('Failed to load admin settings from session storage:', error);
    }
    return {};
  };

  // Save admin settings to session storage
  const saveAdminSettings = (newSettings: Partial<Config>) => {
    try {
      // Only save specific admin settings, not the entire config
      const adminSettings = {
        showMediaInRelatedItemsCloud: newSettings.showMediaInRelatedItemsCloud,
        showGeoInRelatedItemsCloud: newSettings.showGeoInRelatedItemsCloud,
        showIndirectRelationshipsInRing: newSettings.showIndirectRelationshipsInRing,
        terrainExaggeration: newSettings.terrainExaggeration,
      };
      
      // Filter out undefined values
      const filteredSettings = Object.fromEntries(
        Object.entries(adminSettings).filter(([_, value]) => value !== undefined)
      );
      
      const storedSettings = loadAdminSettings();
      const mergedSettings = { ...storedSettings, ...filteredSettings };
      
      sessionStorage.setItem(ADMIN_SETTINGS_KEY, JSON.stringify(mergedSettings));
    } catch (error) {
      console.warn('Failed to save admin settings to session storage:', error);
    }
  };

  const loadDefaultConfig = async (isOrgFallback: boolean = false) => {
    try {
      const defaultResponse = await fetch("/configs/defaultConfig.json");
      if (!defaultResponse.ok) {
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          `Failed to load default config: ${defaultResponse.status}`,
          { variant: 'error', autoHideDuration: 6000 }
        );
        throw new Error(`Failed to load default config: ${defaultResponse.status}`);
      }
      
      const defaultConfig = await defaultResponse.json();
      try {
        validateConfig(defaultConfig);
        
        defaultConfig.initialMapView = defaultConfig.initialMapView || {
          longitude: 133.7751,
          latitude: -25.2744,
          zoom: 3.5,
          pitch: 0,
        };

        // If this is a fallback for an organization without a config,
        // clear potentially sensitive API endpoints to prevent cross-organization data access
        if (isOrgFallback && profile?.organisation?.id) {
          console.warn(`[ConfigContext] Loading safe fallback config for org ${profile.organisation.id} - clearing ${defaultConfig.apiEndpoints?.length || 0} API endpoints to prevent cross-organization data access`);
          defaultConfig.apiEndpoints = [];
          defaultConfig.appTitle = `${defaultConfig.appTitle} - Organization ${profile.organisation.id}`;
          
          // Add a notice about missing configuration
          enqueueUniqueSnackbar(
            enqueueSnackbar,
            `Configuration missing for your organisation. Please contact support to set up your data sources.`,
            { variant: 'info', autoHideDuration: 10000 }
          );
        }

        // Apply stored admin settings if available
        const adminSettings = loadAdminSettings();
        const mergedConfig = { ...defaultConfig, ...adminSettings };

        setConfig(mergedConfig);
        setTheme(createMyTheme(mergedConfig));
        setError(null);
      } catch (validationError) {
        const message = validationError instanceof Error ? validationError.message : 'Invalid default config format';
        console.error('Default config validation error:', validationError);
        setError(message);
        showConfigError(
          "Default config error, using backup configuration", 
          `Default config validation error: ${message}`
        );
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load default config';
      console.error('Failed to load default config:', error);
      setError(message);
      showConfigError(
        "Fatal error loading configuration",
        `Failed to load any configuration: ${message}`
      );
    }
  };

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const configParam = params.get(configUrlParam);

        if (configParam) {
          try {
            // Handle local config case
            if (configParam === 'local' && profile?.organisation?.id) {
              const localConfigUrl = `/customers/${profile.organisation.id}/config.json`;
              try {
                const response = await fetch(localConfigUrl);
                
                if (!response.ok) {
                  console.warn(`Organisation config not found for org ${profile.organisation.id}, falling back to default`);
                  enqueueUniqueSnackbar(
                    enqueueSnackbar,
                    `No configuration found for your organisation (ID: ${profile.organisation.id}). Using default settings.`,
                    { variant: 'warning', autoHideDuration: 8000 }
                  );
                  await loadDefaultConfig(true);
                  return;
                }

                const localConfig = await response.json();
                try {
                  validateConfig(localConfig);
                  setConfig(localConfig);
                  setTheme(createMyTheme(localConfig));
                  setError(null);
                } catch (validationError) {
                  const message = validationError instanceof Error ? validationError.message : 'Unknown validation error';
                  console.error('Organisation config validation error:', validationError);
                  showConfigError(
                    "Organisation config error, using default config", 
                    `Configuration validation failed: ${message}`
                  );
                  await loadDefaultConfig();
                }
                return;
              } catch (fetchError) {
                console.error('Error fetching local config:', fetchError);
                enqueueUniqueSnackbar(
                  enqueueSnackbar,
                  `Error loading organisation config`,
                  { variant: 'error', autoHideDuration: 5000 }
                );
                await loadDefaultConfig();
                return;
              }
            }

            // Handle other config URLs
            try {
              const configUrl = configParam.startsWith('http') 
                ? configParam 
                : `/${configParam.startsWith('/') ? configParam.slice(1) : configParam}`;
              
              try {
                const response = await fetch(configUrl);

                if (!response.ok) {
                  enqueueUniqueSnackbar(
                    enqueueSnackbar,
                    `Failed to load config, falling back to default`,
                    { variant: 'warning', autoHideDuration: 5000 }
                  );
                  await loadDefaultConfig();
                  return;
                }

                const loadedConfig = await response.json();
                try {
                  validateConfig(loadedConfig);
                  setConfig(loadedConfig);
                  setTheme(createMyTheme(loadedConfig));
                  setError(null);
                } catch (validationError) {
                  const message = validationError instanceof Error ? validationError.message : 'Unknown validation error';
                  console.error('Config validation error:', validationError);
                  showConfigError(
                    "Config error, using default config", 
                    `Configuration validation failed: ${message}`
                  );
                  await loadDefaultConfig();
                }
                return;
              } catch (fetchError) {
                console.error('Error fetching config:', fetchError);
                enqueueUniqueSnackbar(
                  enqueueSnackbar,
                  `Error loading config`,
                  { variant: 'error', autoHideDuration: 5000 }
                );
                await loadDefaultConfig();
                return;
              }
            } catch (error) {
              console.error('Error processing custom config URL:', error);
              enqueueUniqueSnackbar(
                enqueueSnackbar,
                `Error processing config URL`,
                { variant: 'error', autoHideDuration: 5000 }
              );
              await loadDefaultConfig();
              return;
            }
          } catch (error) {
            console.error('Top-level error handling config param:', error);
            enqueueUniqueSnackbar(
              enqueueSnackbar,
              `Config loading error`,
              { variant: 'error', autoHideDuration: 5000 }
            );
            await loadDefaultConfig();
            return;
          }
        }

        // Try CDN config if no local config specified
        if (profile?.organisation?.id) {
          try {
            const cdnUrl = `${process.env.REACT_APP_CLOUDFRONT_URL}/org_${profile.organisation.id}/config.json`;
            try {
              const response = await fetch(cdnUrl, {
                credentials: 'include',
                mode: 'cors',
              });
              
              if (!response.ok) {
                console.warn(`Organisation config not found in CDN for org ${profile.organisation.id}, falling back to default`);
                enqueueUniqueSnackbar(
                  enqueueSnackbar,
                  `No configuration found for your organisation (ID: ${profile.organisation.id}). Using default settings.`,
                  { variant: 'warning', autoHideDuration: 8000 }
                );
                await loadDefaultConfig(true);
                return;
              }

              const orgConfig = await response.json();
              try {
                validateConfig(orgConfig);
                setConfig(orgConfig);
                setTheme(createMyTheme(orgConfig));
                setError(null);
              } catch (validationError) {
                const message = validationError instanceof Error ? validationError.message : 'Unknown validation error';
                console.error('CDN config validation error:', validationError);
                showConfigError(
                  "Organisation config error, using default config", 
                  `Configuration validation failed: ${message}`
                );
                await loadDefaultConfig();
              }
              return;
            } catch (fetchError) {
              console.error('Error fetching CDN config:', fetchError);
              enqueueUniqueSnackbar(
                enqueueSnackbar,
                `Error loading organisation config from CDN`,
                { variant: 'error', autoHideDuration: 5000 }
              );
              await loadDefaultConfig();
              return;
            }
          } catch (error) {
            console.error('Error handling CDN config:', error);
            enqueueUniqueSnackbar(
              enqueueSnackbar,
              `CDN config error`,
              { variant: 'error', autoHideDuration: 5000 }
            );
            await loadDefaultConfig();
            return;
          }
        }

        await loadDefaultConfig();
      } catch (error) {
        console.error('Fatal error in config loading:', error);
        showConfigError(
          "Fatal error in configuration loading", 
          `A fatal error occurred when loading configuration: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
        await loadDefaultConfig();
      }
    };

    if (isInitialized) {
      loadConfig();
    }
  }, [configUrlParam, profile, isInitialized, enqueueSnackbar, closeSnackbar]);

  const updateConfig = (updates: Partial<Config>) => {
    if (!config) return;
    
    try {
      // Create a new config object with the updates
      const newConfig = { ...config, ...updates };
      
      // Validate the new config
      validateConfig(newConfig);
      
      // Apply the validated updates
      setConfig(newConfig);
      setTheme(createMyTheme(newConfig));

      // Save admin settings to session storage
      saveAdminSettings(updates);
    } catch (error) {
      console.error('Config update validation error:', error);
      showConfigError(
        "Failed to update configuration", 
        `Configuration update validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  if (!config || !theme) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        {error && (
          <Typography color="error" sx={{ ml: 2 }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // Create a context value that combines the config properties with the update function
  const contextValue: ConfigContextType = {
    ...config,
    updateConfig,
  };

  return (
    <ConfigContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ConfigContext.Provider>
  );
};
